import { styled } from '@linaria/react';
import {
  Checkbox,
  Divider,
  Flex,
  Group,
  Modal,
  MultiSelect,
  NumberInput,
  Select,
  Stack,
  Switch,
  TextInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { UseQueryOptions } from '@tanstack/react-query';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useInitiatives } from '../../../api/initiative-client/initiative-client.hooks';
import type { Initiative } from '../../../api/initiative-client/initiative-client.type';
import type { Target, TargetPayload } from '../../../api/targets-client/targets-client.type';
import { TargetComparison } from '../../../api/targets-client/targets-client.type';
import { TimeAllocationType } from '../../../api/work-periods-client/work-periods-client.type';
import { formatDateRange, STANDARD_DATE_FORMATS } from '../../../helpers/date-time-helpers';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { secondaryBase, skyBase, skyDark } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Icon } from '../../../ui-library/icon/icon';
import { Tooltip } from '../../../ui-library/tooltip/tooltip';
import { Text } from '../../../ui-library/typography/typography';
import { useAvailableObjects, useTargetAvailableMeasures, useTargetForm } from './target-modal.hooks';
import {
  buildComboboxOptions,
  getTargetComparisonLabel,
  getTargetComparisonSymbol,
  getTimeAllocationTypeOptions,
} from './targets.helpers';
import { OBJECT_TYPES, TARGET_THRESHOLD_COLORS, TargetThreshold } from './targets.type';

import { formatDate } from '../../../helpers/date-time-helpers';

interface TargetModalProps {
  opened: boolean;
  onClose: () => void;
  onSave: (data: Partial<TargetPayload>) => void;
  initialData?: Partial<Target>;
  isCreating?: boolean;
  isLoading?: boolean;
  includeDashboardMeasures?: boolean;
}

// Module-level label JSX would freeze the i18n.t() lookup at import time —
// lift into a render-context helper so the language-subscribed t() drives
// the rendered tooltip text on each render.
function ThresholdLabel({ side }: { side: 'Lower' | 'Upper' }) {
  const i18n = useTranslation();
  const description =
    side === 'Lower'
      ? `Lower ${i18n.t('targets.threshold').toLocaleLowerCase()}. When upper is provided and lower is not defined, the lower will default to negative infinity.`
      : `Upper ${i18n.t('targets.threshold').toLocaleLowerCase()}. When lower is provided and upper is not defined, the upper will default to infinity.`;
  return (
    <Tooltip label={description} variant="black" multiline w={300} zIndex={1000}>
      <Group gap={5} style={{ cursor: 'default' }}>
        <Text size="small">{side}</Text>
        <Icon name="info" size={16} color={secondaryBase} />
      </Group>
    </Tooltip>
  );
}

export const TargetModal: FC<TargetModalProps> = ({
  opened,
  onClose,
  onSave,
  initialData,
  isCreating = false,
  isLoading = false,
  includeDashboardMeasures = false,
}) => {
  const i18n = useTranslation();
  const {
    step,
    setStep,
    formData,
    setFormData,
    formErrors,
    handleClose,
    handleSubmit,
    handleObjectTypeChange,
    isWorkPeriodTarget,
  } = useTargetForm(opened, initialData as Target, onSave, onClose);

  const portfolio = useGlobalStore((state) => state.portfolio);
  const { data: availableMeasuresData } = useTargetAvailableMeasures(
    isWorkPeriodTarget,
    formData,
    includeDashboardMeasures,
  );
  const { initiatives = [] } = useInitiatives(portfolio?.id, {
    enabled: !!portfolio?.id,
  } as UseQueryOptions<Initiative[]>);
  const availableObjects = useAvailableObjects(formData.object_type, initiatives);
  const comparisonOptions = Object.values(TargetComparison).map((comparison) => ({
    value: comparison,
    label: getTargetComparisonLabel(comparison),
  }));

  const timeAllocationOptions = getTimeAllocationTypeOptions(formData.object_type);

  const renderStep1 = () => {
    return (
      <Stack gap="md">
        <Text size="regular">
          {isCreating ? 'Create a new target to track your metrics.' : 'Update your target settings below.'}
        </Text>
        <TextInput
          label="Target Name"
          value={formData.name || ''}
          onChange={(e) => setFormData({ ...formData, name: e.currentTarget.value })}
          placeholder="Sprint Velocity Target"
        />
        <Group grow>
          <Select
            comboboxProps={{ withinPortal: true, zIndex: 1000, position: 'top' }}
            label="Object Type"
            data={OBJECT_TYPES}
            value={formData.object_type}
            onChange={handleObjectTypeChange}
            placeholder="Select object type"
            error={formErrors.object_type}
            required
          />
          <MultiSelect
            label="Objects"
            data={buildComboboxOptions(
              availableObjects.map((object) => ({
                value: object.id,
                label: object.name,
              })),
            )}
            value={formData.objects.map((object) => object.id)}
            onChange={(value) =>
              setFormData({
                ...formData,
                objects: value.map((id) => {
                  const foundObject = availableObjects.find((object) => object.id === id);
                  return {
                    id,
                    name: foundObject ? foundObject.name : '',
                    start_date: foundObject ? foundObject.start_date : undefined,
                    end_date: foundObject ? foundObject.end_date : undefined,
                  };
                }),
              })
            }
            placeholder="Select objects"
            radius="lg"
            disabled={!formData.object_type}
            error={formErrors.objects}
            required
            searchable
            comboboxProps={{
              withinPortal: true,
              zIndex: 1000,
              position: 'top',
            }}
          />
        </Group>
        {formData.object_type === 'board' && (
          <Tooltip
            variant="black"
            label="If this is a sprint target, this target will be applied to all sprints in the selected board."
            multiline
            w={250}
            zIndex={1000}
          >
            <Checkbox
              label="Is Sprint Target"
              checked={formData.is_work_period_target}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  is_work_period_target: e.currentTarget.checked,
                  measure: '',
                })
              }
            />
          </Tooltip>
        )}
        {formData.objects.length > 0 && (
          <Group justify="space-between" grow>
            <Select
              label="Measure"
              data={buildComboboxOptions(
                (availableMeasuresData ?? []).map((measure) => ({
                  value: measure.measure_name,
                  label: measure.measure_title,
                })),
              )}
              value={formData.measure}
              onChange={(value) => setFormData({ ...formData, measure: value as string })}
              placeholder="Select measure"
              radius="xl"
              required
              error={formErrors.measure}
              disabled={!formData.object_type}
              comboboxProps={{ withinPortal: true, position: 'top', zIndex: 1000 }}
              searchable
            />
          </Group>
        )}
      </Stack>
    );
  };

  const renderStep2 = () => {
    return (
      <Stack gap="md">
        {formData.object_type === 'initiative' &&
          formData.objects.map((object) => (
            <Group align="bottom" gap="xs" key={object.id}>
              <Text size="small" weight="medium">
                {object.name}:
              </Text>
              <Text size="small">{formatDateRange(object.start_date, object.end_date)}</Text>
            </Group>
          ))}

        <Tooltip
          variant="black"
          label="A prorated target will adjust based on the selected frequency. If turned off, the target value will be diplayed as is."
          multiline
          w={250}
          zIndex={1000}
          position="left"
        >
          <div>
            <Switch
              label="Prorate Target"
              checked={formData.use_transform}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  use_transform: e.currentTarget.checked,
                  time_allocation_type: null,
                })
              }
              color={secondaryBase}
            />
          </div>
        </Tooltip>

        {!isWorkPeriodTarget && (
          <Select
            comboboxProps={{ withinPortal: true, zIndex: 1000, position: 'bottom' }}
            label="Target Frequency"
            value={formData.time_allocation_type}
            onChange={(value) =>
              value && setFormData({ ...formData, time_allocation_type: value as TimeAllocationType | null })
            }
            placeholder="Select target frequency"
            data={timeAllocationOptions}
            required
            error={formErrors.time_allocation_type}
            disabled={formData.is_work_period_target || !formData.use_transform}
          />
        )}

        <Group grow>
          <Select
            comboboxProps={{ withinPortal: true, zIndex: 1000, position: 'top' }}
            maxDropdownHeight={150}
            label="Comparison Type"
            value={formData.target_comparison}
            onChange={(value) => value && setFormData({ ...formData, target_comparison: value as TargetComparison })}
            data={comparisonOptions}
            required
          />
          <NumberInput
            label="Target Value"
            placeholder="Enter target value"
            value={formData.target_value}
            onChange={(value) => setFormData({ ...formData, target_value: Number(value) })}
            required
            error={formErrors.target_value}
          />
          {(formData.target_comparison === TargetComparison.RANGE_IN ||
            formData.target_comparison === TargetComparison.RANGE_EX) && (
            <NumberInput
              label="Upper Target Value"
              placeholder="Enter upper target value"
              value={formData.target_value_upper ?? undefined}
              onChange={(value) => setFormData({ ...formData, target_value_upper: Number(value) })}
              required
              error={formErrors.target_value_upper}
              min={formData.target_value || 0}
            />
          )}
        </Group>
        <Group justify="space-between" grow>
          <DatePickerInput
            required
            radius="xl"
            label="Start Date"
            placeholder="Select start date"
            leftSection={<Icon name="calendar_month" color={secondaryBase} size={16} />}
            leftSectionPointerEvents="none"
            value={formData.start_date ? formatDate(formData.start_date) : undefined}
            maxDate={formData.end_date ? formatDate(formData.end_date) : undefined}
            onChange={(value) =>
              setFormData({ ...formData, start_date: value ? formatDate(value, { format: 'YYYY-MM-DD' }) : '' })
            }
            pointer
            miw="fit-content"
            popoverProps={{ withinPortal: true, zIndex: 1001, position: 'top' }}
            valueFormat={STANDARD_DATE_FORMATS.FULL_DATE}
          />
          <DatePickerInput
            radius="xl"
            label="End Date"
            placeholder="Select end date"
            leftSection={<Icon name="calendar_month" color={secondaryBase} size={16} />}
            leftSectionPointerEvents="none"
            value={formData.end_date ? formatDate(formData.end_date) : undefined}
            minDate={formData.start_date ? formatDate(formData.start_date) : undefined}
            onChange={(value) =>
              setFormData({ ...formData, end_date: value ? formatDate(value, { format: 'YYYY-MM-DD' }) : null })
            }
            pointer
            miw="fit-content"
            clearable
            defaultValue={
              formData.object_type === 'initiative' ? formatDate(formData.objects?.[0]?.end_date ?? '') : undefined
            }
            popoverProps={{ withinPortal: true, zIndex: 1001, position: 'top' }}
            valueFormat={STANDARD_DATE_FORMATS.FULL_DATE}
          />
        </Group>
      </Stack>
    );
  };

  const getThresholdValue = (field: string): number | undefined => {
    const value = formData[field as keyof typeof formData];
    return typeof value === 'number' ? value : undefined;
  };

  const renderStep3 = () => {
    return (
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Text size="small">
            {`Set ${i18n.t('targets.thresholds')} for the target to be considered ${i18n.t('targets.green')}, ${i18n.t('targets.yellow')}, or ${i18n.t('targets.red')}. (Optional)`}
          </Text>
          <Button
            variant="outline"
            radius="xl"
            size="sm"
            w={100}
            justify="left"
            leftSection={<Icon name="cancel" size={16} color={skyDark} style={{ cursor: 'pointer' }} />}
            onClick={() =>
              setFormData({
                ...formData,
                green_threshold_lower: undefined,
                green_threshold_upper: undefined,
                yellow_threshold_lower: undefined,
                yellow_threshold_upper: undefined,
                red_threshold_lower: undefined,
                red_threshold_upper: undefined,
              })
            }
          >
            Clear
          </Button>
        </Group>
        <Divider />
        {[
          {
            label: i18n.t('targets.green'),
            lower: 'green_threshold_lower',
            upper: 'green_threshold_upper',
            color: TARGET_THRESHOLD_COLORS[TargetThreshold.GREEN],
          },
          {
            label: i18n.t('targets.yellow'),
            lower: 'yellow_threshold_lower',
            upper: 'yellow_threshold_upper',
            color: TARGET_THRESHOLD_COLORS[TargetThreshold.YELLOW],
          },
          {
            label: i18n.t('targets.red'),
            lower: 'red_threshold_lower',
            upper: 'red_threshold_upper',
            color: TARGET_THRESHOLD_COLORS[TargetThreshold.RED],
          },
        ].map(({ label, lower, upper, color }, index) => {
          const lowerValue = getThresholdValue(lower);
          const upperValue = getThresholdValue(upper);
          return (
            <Group key={index} gap="md" justify="space-between">
              <Group gap="xs">
                <Icon name="circle" variant="filled" color={color} size={16} />
                <Text>{label}</Text>
              </Group>
              <Stack gap="xs">
                <Group gap="md">
                  <NumberInput
                    label={<ThresholdLabel side="Lower" />}
                    placeholder={`Enter lower ${i18n.t('targets.threshold').toLocaleLowerCase()}`}
                    w="200px"
                    value={lowerValue ?? ''}
                    max={upperValue}
                    onChange={(value) => setFormData({ ...formData, [lower]: Number(value) })}
                    error={formErrors[lower as keyof typeof formErrors]}
                  />
                  <NumberInput
                    label={<ThresholdLabel side="Upper" />}
                    placeholder={`Enter upper ${i18n.t('targets.threshold').toLocaleLowerCase()}`}
                    w="200px"
                    value={upperValue ?? ''}
                    min={lowerValue}
                    onChange={(value) => setFormData({ ...formData, [upper]: Number(value) })}
                    error={formErrors[upper as keyof typeof formErrors]}
                  />
                </Group>
              </Stack>
            </Group>
          );
        })}
      </Stack>
    );
  };
  return (
    <TargetModalStyled
      opened={opened}
      onClose={handleClose}
      title={
        <Stack gap="xs" style={{ marginTop: step > 1 ? '16px' : '0px' }}>
          <Flex align="center" gap="sm">
            <Text weight="bold" size="large">
              {isCreating ? 'Create New Target' : 'Edit Target'}
            </Text>
            <Text size="large" weight="medium" color={skyBase}>
              (Step {step} of 3)
            </Text>
          </Flex>
          {step > 1 && (
            <Text size="medium" weight="medium">
              {formData.name}
              {step === 3 &&
                ` ${getTargetComparisonSymbol(
                  formData.target_comparison,
                  formData.target_value,
                  formData.target_value_upper,
                )}`}
            </Text>
          )}
        </Stack>
      }
      size="lg"
      centered
    >
      <form onSubmit={handleSubmit}>
        {step === 1 ? renderStep1() : step === 2 ? renderStep2() : renderStep3()}
        <Divider style={{ marginTop: '32px' }} />
        <Flex justify="space-between" mt="xl">
          <div>
            {step > 1 && (
              <Button
                variant="outline"
                radius="xl"
                onClick={() => setStep(step - 1)}
                leftSection={<Icon name="arrow_back" size={16} color={skyBase} />}
              >
                Back
              </Button>
            )}
          </div>
          <Flex gap="md">
            <Button variant="outline" radius="xl" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              radius="xl"
              variant="secondary"
              rightSection={<Icon name={step < 3 ? 'arrow_forward' : 'check_circle'} size={16} color="white" />}
              loading={isLoading}
              disabled={isLoading}
            >
              {step < 3 ? 'Next Step' : isCreating ? 'Create Target' : 'Save Changes'}
            </Button>
          </Flex>
        </Flex>
      </form>
    </TargetModalStyled>
  );
};

const TargetModalStyled = styled(Modal)`
  .mantine-Modal-content {
    border-radius: 12px;
    background: white;
    border: 1px solid #e9ecef;
  }

  .mantine-Modal-header {
    padding: 12px 24px 0 24px;
  }

  .mantine-Modal-body {
    padding: 24px;
  }

  .mantine-Modal-close {
    color: #868e96;
    &:hover {
      background-color: #f1f3f5;
    }
  }

  .mantine-TextInput-input,
  .mantine-Select-input,
  .mantine-NumberInput-input {
    border-radius: 16px;
    transition: border-color 0.2s ease;
    z-index: 1000;

    &:focus {
      border-color: ${secondaryBase};
    }
  }
`;
